import React, { useState, useEffect, useMemo } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import { useRouteMatch, useLocation, useHistory } from 'react-router-dom'
import { Container, Box, Hidden, Avatar } from '@material-ui/core'
import { Tabs, Tab } from 'components/tabs'
import PageHeader from 'components/page-header/page-header'
import { formatNameForDisplay, getInitials } from 'lib/text-display'
import StudentDetails from './student-details'
import StudentSessions from './student-sessions'
import StudentActionButtons from './header-components/action-buttons'
import { useIsMobile, useIsTablet } from 'lib/hooks'
import { useGetStatistics, useGetSessions } from './api-hooks'
import { useAuth } from 'lib/auth'
import EventOutlinedIcon from 'components/material-symbol/icons/Event'
import PeopleOutlinedIcon from 'components/material-symbol/icons/People'
import IndexCard from './mobile-index-card'
import { gtmEvent } from 'lib/gtm'
import DisplayContact from './details-components/display-contact'
import DeleteStudent from './details-components/delete-student'
import LessonPacks from './lesson-packs'
import GolferProfileCta from './golfer-profile-cta'
import { formatStudentHeaderMeta } from './utils'

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100%',
    margin: 0,
    paddingBottom: '16px',
  },
  rootWithStickyActions: {
    paddingBottom: theme.spacing(10),
    [theme.breakpoints.down('xs')]: {
      paddingBottom: theme.spacing(12),
    },
  },
  studentHeaderAvatar: {
    width: 64,
    height: 64,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    fontSize: '18px',
    fontWeight: 500,
  },
  stickyActionBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.appBar,
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1.5, 2, 2),
    backgroundColor: theme.palette.common.white,
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    boxSizing: 'border-box',
  },
  stickyActionBarInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  mobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  mobileDetailsGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  mobileNavStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}))

export default function StudentShowPage({ student, refetch }) {
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const classes = useStyles()

  // ----> NAVIGATION LOGIC.
  const { path } = useRouteMatch()
  const { search } = useLocation()
  const { user } = useAuth()
  const { hideSensitiveInformation } = user
  const history = useHistory()
  const [activeTab, setActiveTab] = useState(false)

  const statsPayload = useGetStatistics(student?.id)
  const studentSessions = useGetSessions(student?.id, user?.id)
  const studentName = formatNameForDisplay(
    `${student?.firstName} ${student?.lastName}`,
  )
  const purchasedlessonPacks = student?.contact.lessonPacks.filter(
    lessonPack => lessonPack.lessonType.coach.id === user.coach.id,
  )

  const giftedLessonPacks = student?.contact.giftedLessonPacks.filter(
    lessonPack => lessonPack.lessonType.coach.id === user.coach.id,
  )

  const updatedGiftedLessonPacks = giftedLessonPacks.map(pack => ({
    ...pack,
    remainingCredits: pack.remainingGiftedCredits,
  }))
  const lessonPacks = [...purchasedlessonPacks, ...updatedGiftedLessonPacks]

  const headerMeta = useMemo(() => {
    const enrollments = studentSessions?.data?.studentEnrollments ?? []
    return formatStudentHeaderMeta(enrollments)
  }, [studentSessions])

  useEffect(() => {
    const queryParams = new URLSearchParams(search)
    const tab = queryParams.get('tab')

    /*
      We'll append ?tab=details or ?tab=cool-things or whatever
      to track the open tab, rather than mucking with a bunch of
      new routes. This also prevents us from having an invalid tab.
    */

    switch (tab) {
      case 'details':
        setActiveTab('details')
        break
      case 'sessions':
        setActiveTab('sessions')
        break
      case 'lessonPacks':
        setActiveTab('lessonPacks')
        break
      default:
        !isMobile ? setActiveTab('details') : setActiveTab(false)
    }
  }, [search, isMobile])

  const URL = `${path
    .split('/')
    .slice(0, 3)
    .join('/')}/${student.id}`

  const crumbs = [
    {
      label: 'Students',
      to: `${path
        .split('/')
        .slice(0, 2)
        .join('/')}/students/list`,
    },
    {
      label: studentName,
      to: URL,
    },
  ]

  const onSessionsClick = () => {
    gtmEvent({
      event: 'student-page-tab-click',
      activitiesCategory: 'student-sessions',
      activitiesAction: 'sessions-click',
      activitiesLabel: 'sessions-click',
    })
  }

  const onLessonPacksClick = () => {
    gtmEvent({
      event: 'student-page-tab-click',
      activitiesCategory: 'student-lesson-packs',
      activitiesAction: 'lesson-packs-click',
      activitiesLabel: 'lesson-packs-click',
    })
  }

  const onDetailsClick = () => {
    gtmEvent({
      event: 'student-page-tab-click',
      activitiesCategory: 'student-details',
      activitiesAction: 'details-click',
      activitiesLabel: 'details-click',
    })
  }

  const handleMobileCardClick = value => {
    setActiveTab(value)
    if (value === 'sessions') {
      onSessionsClick()
    } else if (value === 'details') {
      onDetailsClick()
    }
    history.push(`${URL}?tab=${value}`)
  }

  const tabs = (
    <Tabs
      compact
      value={activeTab}
      onChange={(_, value) => {
        if (value === 'details') onDetailsClick()
        if (value === 'sessions') onSessionsClick()
        if (value === 'lessonPacks') onLessonPacksClick()
        history.push(`${URL}?tab=${value}`)
      }}
    >
      <Tab
        label="Details"
        value="details"
        data-testid="student-show-tab-details"
      />
      <Tab
        label="Sessions"
        value="sessions"
        data-testid="student-show-tab-sessions"
      />
      <Tab
        label="Lesson Packs"
        value="lessonPacks"
        data-testid="student-show-tab-lessonPacks"
      />
    </Tabs>
  )

  function DisplayedTab() {
    if (activeTab === 'details') {
      return <StudentDetails student={student} stats={statsPayload} />
    } else if (activeTab === 'sessions') {
      return <StudentSessions sessions={studentSessions} student={student} />
    } else if (activeTab === 'lessonPacks') {
      return <LessonPacks lessonPacks={lessonPacks} refetch={refetch} />
    } else {
      return isMobile ? (
        <MobileDisplay />
      ) : (
        <StudentDetails student={student} stats={statsPayload} />
      )
    }
  }

  function MobileDisplay() {
    return (
      <Container className={classes.mobile}>
        <GolferProfileCta student={student} />
        <Box className={classes.mobileDetailsGroup}>
          <DisplayContact
            isMinor={student?.isMinor}
            contact={student?.contact}
          />
          <Box className={classes.mobileNavStack}>
            <IndexCard
              title="Student Details"
              caption="View contact info, demographics, and emergency details."
              value="details"
              icon={PeopleOutlinedIcon}
              onClick={handleMobileCardClick}
            />
            <IndexCard
              title="Student Sessions"
              caption="View upcoming and past sessions."
              value="sessions"
              icon={EventOutlinedIcon}
              onClick={handleMobileCardClick}
            />
          </Box>
        </Box>
        {isMobile && !hideSensitiveInformation && (
          <DeleteStudent student={student} />
        )}
      </Container>
    )
  }

  return (
    <Box
      className={clsx(classes.root, isTablet && classes.rootWithStickyActions)}
    >
      <PageHeader
        data-testid="student-header"
        title={
          isMobile && activeTab
            ? `${studentName} ${formatNameForDisplay(activeTab)}`
            : studentName
        }
        crumbs={crumbs}
        hideCrumbs
        avatar={
          <Hidden xsDown>
            <Avatar className={classes.studentHeaderAvatar}>
              {getInitials(studentName)}
            </Avatar>
          </Hidden>
        }
        subtitle={!isMobile || !activeTab ? headerMeta : null}
        backTo={
          isMobile && activeTab ? crumbs[crumbs.length - 1].to : crumbs[0].to
        }
        tabs={!isMobile ? tabs : null}
        actions={
          <Hidden smDown>
            <StudentActionButtons student={student} />
          </Hidden>
        }
      />
      {DisplayedTab()}
      {isTablet ? (
        <Box className={classes.stickyActionBar}>
          <Box className={classes.stickyActionBarInner}>
            <StudentActionButtons student={student} useStickyIcons />
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}
